// Which saved definition rows need browser finalization before a catalog table may show them.
// The catalog's saved-only definition families (definition-list-rows.ts) call this to decide whether to refetch the
// endpoint with `inputs=full` and finalize. It lives in its own module so a catalog change never edits the shared
// finalizer that engine bundles pin to served bytes.
// ⚠ DRIFT: keep this in step with createFromBaseDefinitionFinalizer (from-base-definition-finalization.ts) and
// createClassificationContext (from-base-classification-context.ts). When either starts reading another saved input,
// add it here. A false positive only costs payload; a false negative shows an unfinalized row.
import { readItemUuidSlot } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { fromBaseRecord } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import { phpJsonTruthy } from './php-json-values';
import { deriveDefaultProviderNames } from './provider-catalog-order';
import { DEFAULT_PROVIDER_LAYERS } from '../../shared/array-types';

/** Mirror of the classification type set inlined in createClassificationContext
 * (tools/test/definition-list-finalization.test.mjs asserts the two stay equal). */
export function definitionClassificationTypes(objectTypes: readonly string[]): ReadonlySet<string> {
    return new Set([...objectTypes, 'creature', 'lineage', 'map_marker',
        'creature_template', 'creature_sub_type', 'creature_ancestry', 'creature_grouping', 'creature_lineage',
        'creature_sub_type_ancestral']);
}

export interface DefinitionFinalizationReads {
    layers: readonly string[];
    pictures?: boolean;
    allLayers?: boolean;
    nestedReferences?: readonly string[];
}
export interface DefinitionFinalizationMetadata {
    objectTypes: FromBaseInventoryMetadata['objectTypes'];
    layerCatalog: Readonly<Record<string, Pick<FromBaseInventoryMetadata['layerCatalog'][string], 'default_provider' | 'full_nest'> & { empty_refs?: readonly string[] }>>;
}

/** Without a read scope this conservatively detects definition-changing inputs.
 * A list supplies its display/sort/filter reads: changes to unobserved caches,
 * assets or nested carriers alone do not require a second table request. */
export function definitionNeedsFinalization(row: Row, reads?: DefinitionFinalizationReads,
    metadata?: DefinitionFinalizationMetadata, catalog?: Readonly<Record<string, Row>>): boolean {
    const own = (key: string): unknown => readItemField({ [key]: [
        (fromBaseRecord(row.item_layer_types) ? row.item_layer_types[key] : undefined) ?? row[key] ?? null,
    ] }, key);
    const present = (key: string): boolean => phpJsonTruthy(own(key));
    // createClassificationContext.intrinsicEnchantment -> applyFromBaseIntrinsicEnchantment.
    // Keep this unconditional: templates can alter authored values, not only missing ones.
    if (present('enchantment_template_applied')) return true;
    const layerCatalog = metadata?.layerCatalog;
    const providers = layerCatalog ? deriveDefaultProviderNames(layerCatalog) : DEFAULT_PROVIDER_LAYERS;
    const type = String(readItemField(row, 'item_type'));
    const classified = metadata ? definitionClassificationTypes(metadata.objectTypes).has(type) : true;
    const layerReads = !reads || reads.allLayers || reads.layers.length > 0;
    // dynamicClassification and finalize's own-size prelude stamp Tiny before
    // inherited size can suppress it. An omitted classification catalog is unknown.
    if (classified && (!reads || reads.allLayers || reads.layers.includes('size')) && !present('size')) return true;
    // dynamicClassification merges non-full-nest lock/material sources; full-nest
    // carriers instead retain their own fields and can affect computed vitality.
    for (const key of ['lock_type', 'item_composition']) if (present(key)
        && (!reads || (layerReads && !layerCatalog?.[key]?.full_nest))) return true;
    // constructInventoryDefinitionChoices bakes labels from these saved edges.
    if (present('ability_choice') && (!reads || reads.allLayers || reads.layers.includes('ability_choice_options'))) return true;
    // constructClassificationFromBase replaces a creature's gallery from its model.
    if (type === 'creature' && present('creature_model') && (!reads || reads.pictures)) return true;
    for (const key of providers) {
        if (!present(key)) continue;
        if (!reads) return true;
        // Provider reads precede reference normalization. A static identity
        // shell is not evidence that its authored donor carries no inputs.
        const spec = layerCatalog?.[key];
        const value = own(key);
        const entries = Array.isArray(value) ? value : fromBaseRecord(value) && '0' in value
            && !fromBaseRecord(value.items) ? Object.values(value) : [value];
        // Every donor must be proven empty. The first empty donor must never
        // hide another donor's authored layers, gallery or classified defaults.
        const emptyLeaves = spec?.full_nest ? entries.map(entry => {
            if (typeof entry !== 'string') return null;
            const ref = readItemUuidSlot(entry), leaf = catalog?.[ref];
            return spec.empty_refs?.includes(ref) && leaf
                && !definitionClassificationTypes(metadata!.objectTypes).has(String(readItemField(leaf, 'item_type'))) ? leaf : null;
        }) : [];
        if (emptyLeaves.length && emptyLeaves.every((leaf): leaf is Row => leaf !== null)) {
            if (reads.allLayers) return true;
            // Any live donor picture may be visible to some viewer, private ones included.
            if (reads.pictures && !hasOwnPictures(row) && emptyLeaves.some(leaf => hasOwnPictures(leaf, true))) return true;
            continue;
        }
        if (layerReads) return true;
        // A creature's inherited model can replace even its own gallery.
        if (reads.pictures && (type === 'creature' || !hasOwnPictures(row))) return true;
    }
    // constructInventoryDefinitionReferences fully expands these references;
    // projectors that inspect child layers need more than the saved identity hop.
    if (reads?.nestedReferences?.some(key => present(key))) return true;
    return false;
}

function hasOwnPictures(row: Row, includePrivate = false): boolean {
    return Array.isArray(row.pictures) && row.pictures.some(picture => fromBaseRecord(picture)
        && readItemField(picture, 'picture_attached_item_type') === 'item'
        && Number(readItemField(picture, 'picture_deleted') ?? 0) === 0
        // Private galleries are viewer-dependent; only public own pictures
        // prove that a provider gallery cannot become the visible fallback.
        && (includePrivate || !phpJsonTruthy(readItemField(picture, 'picture_private'))));
}
